import type { ParsedToken } from "@/lib/search";

/**
 * Appears **only when the hovered token is inflected**, and has **no add button, ever**.
 *
 * From the design's own annotation: "The FORM strip is not a result… it exists to tell you why the
 * text doesn't match the dictionary." Cards are only ever made from the dictionary form, which is
 * the first result below it.
 *
 * ## Its chips are lookups now (日本-512)
 *
 * That rule is intact and this does not bend it: a chip **pivots the dock**, it does not add. What
 * changed is that the roots the strip has always drawn stopped being decoration.
 *
 * `DisplayToken.components` has always carried each collapsed sub-token's *dictionary form*, and
 * `_token_dto` has always sent it — so hovering 食べてしまった drew
 * `食べてしまった ← 食べる ＋ て ＋ しまう ＋ た`, with **しまう on screen, correctly deinflected,
 * and inert**: measured in the DOM every chip was `cursor: auto`, `role: null`, `tabIndex: -1`.
 *
 * Measured over 1,566 of Alex's own captured sentences, **758 (48.4%) draw at least one chip whose
 * root is a real dictionary word** — 1,147 of them, 66 distinct: ない, いる, する, れる, ます,
 * てる, から, けど, くれる, くる, たい, られる, せる, おく, みる, やる. That is exactly the
 * grammar a reader reaches for, and 日本-498's grammar dictionary answers most of it (ない draws 24
 * 文法 rows, する 18, から 16). None of it was reachable, because MeCab folds the auxiliary chain
 * into the host token — so the *character* pivot cannot reach it either, and these chips are the
 * only route.
 *
 * **Every chip is live, and that was measured rather than assumed.** The first cut of this dimmed
 * chips whose root had no dictionary entry, on the assumption that て and た were labels rather
 * than words. They are not: over those same sentences **2,382 chips were drawn and 2,382 had
 * entries — zero dead**. The `hasEntries` guard below therefore fires for nothing today and is
 * kept deliberately, so a dataset change or a form the parser invents degrades to an inert chip
 * rather than to one that lights up and answers nothing. Do not delete it as dead code, and do not
 * expect to see it.
 */
export function FormStrip({
  token,
  activeRoot,
  onPivot,
}: {
  token: ParsedToken;
  /** Which chip the dock is currently answering, if any. */
  activeRoot?: string | null;
  /** Pivot the dock at this root. Absent on a read-only render. */
  onPivot?: (root: string) => void;
}) {
  if (!token.inflected) return null;

  // `componentRoots` is the pairing of each chip with whether the dictionary knows it. It arrived
  // with 日本-512 beside the older `components`, which is display text and still what this draws —
  // so a client and a server that disagree about the new field fall back to inert chips rather
  // than to no strip. Keyed by text rather than by position: a component can appear twice (て in
  // 〜ておいて) and both chips answer the same thing.
  const known = new Map((token.componentRoots ?? []).map((c) => [c.text, c.hasEntries]));

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-[10px] rounded-[var(--r-3)] border border-accent-border bg-accent-ground px-[14px] py-[10px]">
      <span className="rounded-[var(--r-2)] bg-accent-tint px-1.5 py-0.5 text-[14px]/[1.5] font-semibold uppercase tracking-[.06em] text-ink-3">
        活用
      </span>
      <span className="font-jp text-[22px]/[1] text-ink">{token.surface}</span>
      <span className="text-[15px]/[1] text-ink-4">←</span>
      <span className="font-jp text-[22px]/[1] text-ink">{token.dictionaryForm}</span>
      {token.components.map((component, i) => {
        const live = (known.get(component) ?? false) && onPivot !== undefined;
        const active = live && component === activeRoot;
        return (
          <span key={i} className="flex items-center gap-[10px]">
            <span className="text-[15px]/[1] text-ink-4">+</span>
            {live ? (
              <button
                type="button"
                data-check="read.form.chip"
                data-active={active || undefined}
                // The dock is single-tenant and the sentence's own hover would otherwise take it
                // straight back, so this is a CLICK rather than a hover. It is also why the chip
                // keeps the sentence's own selected treatment when active: one screen, one way of
                // saying *this is what the dock is answering*.
                onClick={() => onPivot(component)}
                className={[
                  "rounded-[var(--r-2)] px-2 py-1 font-jp text-[18px]/[1] transition-colors",
                  active
                    ? "border border-transparent bg-accent-tint text-ink-bright shadow-[0_2px_0_var(--accent)]"
                    : "border border-accent-border bg-panel text-ink-2 hover:border-accent-line hover:bg-raised hover:text-accent-text",
                ].join(" ")}
              >
                {component}
              </button>
            ) : (
              <span className="rounded-[var(--r-2)] border border-accent-border bg-panel px-2 py-1 font-jp text-[18px]/[1]">
                {component}
              </span>
            )}
          </span>
        );
      })}
      {token.formDescription && (
        <span className="text-[15px]/[1.4] text-ink-2">{token.formDescription}</span>
      )}
    </div>
  );
}
